import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {DocumentRawModal} from '../memory/DocumentRawModal';
import {SourceViewerContext} from './sourceViewerContext';

/**
 * El único visor del crudo de la página del Booster.
 *
 * Monta UN `DocumentRawModal` y reparte la manija `openSource(document, {quote})` por contexto
 * (`useSourceViewer`). Quien la llama no sabe ni le importa dónde está montado el modal: la carta
 * del mazo, la fila del archivo y el ruido archivado abren el mismo visor con el mismo gesto.
 *
 * Es un `Modal` de reactstrap, así que mientras está abierto `body.modal-open` apaga el teclado
 * del mazo de Revisa (su guarda de siempre) y Escape lo cierra sin rechazar nada.
 */
export function SourceViewerHost({clientId, children}) {
    // `{document, quote}` de lo que se está mirando, o `null`.
    const [target, setTarget] = useState(null);

    // 🔴 Al cambiar de cliente, lo que había abierto se suelta. El Recorrido REUSA este host al
    // navegar de un cliente a otro, así que sin esto el modal podía quedar mostrando el documento
    // del cliente ANTERIOR hasta que el backend contestara 404 — un cruce de datos entre clientes
    // en pantalla, aunque el aislamiento del endpoint lo corte después (review C-cita-03).
    useEffect(() => { setTarget(null); }, [clientId]);

    const openSource = useCallback((document, options) => {
        if (!document) return;
        const quote = options && options.quote ? String(options.quote) : null;
        setTarget({document, quote});
    }, []);
    const close = useCallback(() => setTarget(null), []);

    const api = useMemo(() => ({available: true, openSource}), [openSource]);

    return (
        <SourceViewerContext.Provider value={api}>
            {children}
            <DocumentRawModal
                isOpen={!!target}
                document={target ? target.document : null}
                quote={target ? target.quote : null}
                clientId={clientId}
                toggle={close}
            />
        </SourceViewerContext.Provider>
    );
}

export default SourceViewerHost;
